import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { FaClipboardList, FaGift, FaDollarSign, FaTrophy, FaChartLine, FaCalendarAlt } from 'react-icons/fa';
import axios from 'axios';
import styled from 'styled-components';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'react-toastify';

const DashboardContainer = styled.div`
  max-width: 1000px; // Reduced from 1200px
  margin: 0 auto;
  padding: 1.5rem 0.8rem; // Reduced from 2rem 1rem
  min-height: calc(100vh - 120px);
  font-family: 'Inter', sans-serif;
`;

const WelcomeSection = styled.div`
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
  padding: 1.5rem; // Reduced from 2rem
  border-radius: 10px; // Reduced from 12px
  margin-bottom: 1.5rem; // Reduced from 2rem
  text-align: center;
  
  h1 {
    margin: 0 0 0.4rem 0;
    font-size: 1.6rem; // Reduced from 2rem
    font-weight: 700;
    font-family: 'Inter', sans-serif;
  }
  
  p {
    margin: 0;
    opacity: 0.95;
    font-size: 0.95rem; // Reduced from 1.1rem
    font-weight: 400;
    font-family: 'Inter', sans-serif;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); // Reduced from 250px
  gap: 1.2rem; // Reduced from 1.5rem
  margin-bottom: 1.5rem; // Reduced from 2rem
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.8rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 1.2rem; // Reduced from 1.5rem
  border-radius: 10px; // Reduced from 12px
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 0.8rem; // Reduced from 1rem
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
  
  &:hover {
    transform: translateY(-1px); // Reduced from -2px
    box-shadow: 0 6px 16px rgba(30, 58, 138, 0.15);
  }
`;

const StatIcon = styled.div`
  font-size: 1.5rem; // Reduced from 1.8rem
  color: ${props => props.color || '#3b82f6'};
  background: ${props => props.color || '#3b82f6'}15;
  padding: 0.6rem; // Reduced from 0.7rem
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatContent = styled.div`
  h3 {
    margin: 0 0 0.15rem 0;
    font-size: 1.2rem; // Reduced from 1.4rem
    color: #2c3e50;
  }
  
  p {
    margin: 0;
    color: #7f8c8d;
    font-weight: 500;
    font-size: 0.75rem; // Reduced from 0.85rem
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  gap: 0.8rem; // Reduced from 1rem
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 6px; // Reduced from 8px
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 1px solid #e2e8f0;
`;

const CardHeader = styled.div`
  padding: 0.8rem; // Reduced from 1rem
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin: 0;
    color: #2c3e50;
    font-size: 0.95rem; // Reduced from 1.1rem
  }
`;

const CardContent = styled.div`
  padding: 0.8rem; // Reduced from 1rem
`;

const SurveyItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0; // Reduced from 0.7rem 0
  border-bottom: 1px solid #f0f0f0;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: background-color 0.2s ease;
  border-radius: 4px;
  margin: 0 -0.4rem; // Reduced from 0 -0.5rem
  padding: 0.6rem 0.4rem; // Reduced from 0.7rem 0.5rem
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: ${props => props.$clickable ? 'rgba(30, 58, 138, 0.05)' : 'transparent'};
  }
`;

const SurveyInfo = styled.div`
  h4 {
    margin: 0 0 0.1rem 0;
    color: #2c3e50;
    font-size: 0.8rem; // Reduced from 0.9rem
  }
  
  p {
    margin: 0;
    color: #7f8c8d;
    font-size: 0.7rem; // Reduced from 0.8rem
  }
`;

const PointsBadge = styled.span`
  background: #f39c12;
  color: white;
  padding: 0.2rem 0.6rem; // Reduced from 0.25rem 0.75rem
  border-radius: 12px; // Reduced from 15px
  font-size: 0.7rem; // Reduced from 0.8rem
  font-weight: bold;
`;

const ActionButton = styled(Link)`
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 16px;
  text-decoration: none;
  font-weight: bold;
  font-size: 0.75rem;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.3rem;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(102, 126, 234, 0.3);
  }
  
  svg {
    font-size: 0.75rem;
    flex-shrink: 0;
  }
`;

const RedeemButton = styled.button`
  background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%);
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 16px;
  border: none;
  font-weight: bold;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(46, 204, 113, 0.3);
  }
  
  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    font-size: 0.75rem;
    flex-shrink: 0;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 1rem;
  color: #7f8c8d;
  
  p {
    margin: 0.3rem 0;
    font-size: 0.85rem;
  }
`;



const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const handleSurveyClick = (surveyId) => {
    navigate(`/surveys/${surveyId}`);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/users/dashboard');
      if (response.data.success) {
        console.log('Dashboard data received:', response.data.dashboard);
        console.log('Recent completed surveys:', response.data.dashboard.recentCompletedSurveys);
        if (response.data.dashboard.recentCompletedSurveys && response.data.dashboard.recentCompletedSurveys.length > 0) {
          console.log('First completed survey structure:', response.data.dashboard.recentCompletedSurveys[0]);
          console.log('Survey object in first response:', response.data.dashboard.recentCompletedSurveys[0].Survey);
        }
        setDashboardData(response.data.dashboard);
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      setError('Failed to load dashboard data. Error: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user?.points]); // Refresh dashboard when user points change

  // Additional effect to refresh dashboard when user object changes
  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  // Socket.io listener for real-time point updates
  useEffect(() => {
    if (socket && user) {
      const handlePointsUpdate = async (data) => {
        console.log('Received points update:', data);
        
        // Show notification to user
        if (data.reason === 'withdrawal_approved') {
          toast.success(data.message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } else if (data.reason === 'withdrawal_rejected') {
          toast.info(data.message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
        
        // Refresh user data and dashboard
        await refreshUser();
        await fetchDashboardData();
      };

      socket.on('pointsUpdated', handlePointsUpdate);

      return () => {
        socket.off('pointsUpdated', handlePointsUpdate);
      };
    }
  }, [socket, user, refreshUser, fetchDashboardData]);

  // Handle URL parameters for survey completion refresh
  useEffect(() => {
    const completed = searchParams.get('completed');
    const refresh = searchParams.get('refresh');
    
    if (completed === 'true' && refresh === 'true') {
      // Refresh user data and dashboard when returning from survey completion
      const refreshData = async () => {
        try {
          await refreshUser(); // Update user context with latest points
          await fetchDashboardData(); // Refresh dashboard data including survey lists
          
          // Clean up URL parameters after refresh
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('completed');
          newSearchParams.delete('refresh');
          setSearchParams(newSearchParams, { replace: true });
        } catch (error) {
          console.error('Error refreshing dashboard data:', error);
        }
      };
      
      refreshData();
    }
  }, [searchParams, setSearchParams, refreshUser, fetchDashboardData]);

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingSpinner />
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#e74c3c' }}>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </DashboardContainer>
    );
  }

  const stats = dashboardData?.stats || {
    totalSurveys: 0,
    totalPointsEarned: 0,
    totalRedemptions: 0,
    currentPoints: user?.points || 0
  };

  const recentCompletedSurveys = dashboardData?.recentCompletedSurveys || [];
  const recentOpenSurveys = dashboardData?.recentOpenSurveys || [];

  return (
    <DashboardContainer>
      <WelcomeSection>
        <h1>Welcome back, {user?.firstName || 'Member'}!</h1>
        <p>Here's your survey activity and earnings overview</p>
      </WelcomeSection>

      <StatsGrid>
        <StatCard>
          <StatIcon color="#3b82f6">
            <FaClipboardList />
          </StatIcon>
          <StatContent>
            <h3>{stats.totalSurveys}</h3>
            <p>Surveys Completed</p>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon color="#f39c12">
            <FaDollarSign />
          </StatIcon>
          <StatContent>
            <h3>{stats.currentPoints}</h3>
            <p>Current Points</p>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon color="#27ae60">
            <FaTrophy />
          </StatIcon>
          <StatContent>
            <h3>{stats.totalPointsEarned}</h3>
            <p>Total Points Earned</p>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon color="#e74c3c">
            <FaDollarSign />
          </StatIcon>
          <StatContent>
            <h3>₹{(stats.approvedWithdrawalAmount || 0).toLocaleString()}</h3>
            <p>Total Withdrawals</p>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <ContentGrid>
        <Card>
          <CardHeader>
            <h2>Recent Survey Activity</h2>
            <ActionButton to="/surveys">Browse Surveys</ActionButton>
          </CardHeader>
          <CardContent>
            {/* Completed Surveys Section */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ margin: '0 0 0.8rem 0', fontSize: '0.95rem', color: '#2c3e50', fontWeight: '600' }}>Recently Completed</h3>
              {recentCompletedSurveys && recentCompletedSurveys.length > 0 ? (
                recentCompletedSurveys.slice(0, 1).map((survey, index) => (
                  <SurveyItem key={`completed-${index}`}>
                    <SurveyInfo>
                      <h4>{survey.Survey?.title || 'Untitled Survey'}</h4>
                      <p>
                        <FaCalendarAlt style={{ marginRight: '0.3rem' }} />
                        {(() => {
                          // Use updatedAt as it represents completion time for completed surveys
                          const date = new Date(survey.updatedAt || survey.createdAt);
                          return isNaN(date.getTime()) ? 'Not available' : date.toLocaleDateString();
                        })()}
                      </p>
                    </SurveyInfo>
                    <PointsBadge>+{survey.pointsEarned} pts</PointsBadge>
                  </SurveyItem>
                ))
              ) : (
                <p style={{ fontSize: '0.8rem', color: '#7f8c8d', margin: '0.5rem 0', fontStyle: 'italic' }}>No completed surveys yet</p>
              )}
            </div>
            
            {/* Available Surveys Section */}
            <div>
              <h3 style={{ margin: '0 0 0.8rem 0', fontSize: '0.95rem', color: '#2c3e50', fontWeight: '600' }}>Available Surveys</h3>
              {recentOpenSurveys && recentOpenSurveys.length > 0 ? (
                recentOpenSurveys.slice(0, 1).map((survey, index) => (
                  <SurveyItem 
                    key={`open-${index}`} 
                    $clickable={true}
                    onClick={() => handleSurveyClick(survey.id)}
                  >
                    <SurveyInfo>
                      <h4>{survey.title}</h4>
                      <p>
                        <FaCalendarAlt style={{ marginRight: '0.3rem' }} />
                        {survey.estimatedTime} min • {survey.category || 'General'}
                      </p>
                    </SurveyInfo>
                    <PointsBadge style={{ background: '#27ae60' }}>+{survey.pointsReward} pts</PointsBadge>
                  </SurveyItem>
                ))
              ) : (
                <p style={{ fontSize: '0.8rem', color: '#7f8c8d', margin: '0.5rem 0', fontStyle: 'italic' }}>No available surveys</p>
              )}
            </div>
            
            {(!recentCompletedSurveys || recentCompletedSurveys.length === 0) && (!recentOpenSurveys || recentOpenSurveys.length === 0) && (
              <EmptyState>
                <FaClipboardList size={32} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
                <p style={{ fontSize: '0.85rem', margin: '0.3rem 0' }}>No survey activity yet</p>
                <ActionButton to="/surveys" style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
                  Find Surveys
                </ActionButton>
              </EmptyState>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h2>Quick Actions</h2>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
              <ActionButton to="/surveys">
                <FaClipboardList style={{ marginRight: '0.3rem' }} />
                Take Surveys
              </ActionButton>
              <ActionButton to="/rewards">
                <FaGift style={{ marginRight: '0.3rem' }} />
                Browse Rewards
              </ActionButton>
              <ActionButton to="/profile">
                <FaChartLine style={{ marginRight: '0.3rem' }} />
                View Profile
              </ActionButton>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h2>Rewards & Tips</h2>
          </CardHeader>
          <CardContent>
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.7rem' }}>
                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#2c3e50' }}>Available Points:</span>
                <PointsBadge>{stats.currentPoints} pts</PointsBadge>
              </div>
              <RedeemButton 
                onClick={() => window.location.href = '/rewards'}
                disabled={stats.currentPoints < 100}
              >
                <FaGift style={{ marginRight: '0.3rem' }} />
                {stats.currentPoints >= 100 ? 'Redeem Rewards' : 'Need 100+ Points'}
              </RedeemButton>
            </div>
            <div style={{ fontSize: '0.8rem', lineHeight: '1.4', color: '#7f8c8d' }}>
              <p style={{ margin: '0.3rem 0' }}>💡 Complete profile for more surveys</p>
              <p style={{ margin: '0.3rem 0' }}>🎯 Check daily for new surveys</p>
              <p style={{ margin: '0.3rem 0' }}>⭐ Quality responses = better rating</p>
              <p style={{ margin: '0.3rem 0' }}>🎁 Save points for better rewards</p>
            </div>
          </CardContent>
        </Card>
      </ContentGrid>
    </DashboardContainer>
  );
};

export default Dashboard;